import { DataCard, Spacer, TableTagEnergyRecommendation } from "@qivia/ui";
import { DonutChartDS } from "@qivia/ui/src/designSystem/components/Charts/DonutChart";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppSelector } from "../../../redux/hooks";
import { selectListAllVehiclesInDb } from "../vehicles/vehiclesSlice";
import { VehicleEnergyRecommendation } from "./electrificationApi";
import "../../../i18n/i18n";

// TO REMOVE LATER — mocked recommendations assigned by index, matching what the
// vehicles table displays (first 5 vehicles), so the header counts stay aligned.
const fakeEnergyRecommendations: VehicleEnergyRecommendation[] = [
  "ELECTRIC",
  "PLUGIN_HYBRID",
  "NOT_ELIGIBLE",
  "INSUFFICIENT_DATA",
  "ELECTRIC",
];

export const VehiclesContentHeader = () => {
  const { t } = useTranslation();
  const vehiclesListInit = useAppSelector(selectListAllVehiclesInDb);

  const recommendations = useMemo(
    () =>
      vehiclesListInit
        .slice(0, fakeEnergyRecommendations.length)
        .map((_, index) => fakeEnergyRecommendations[index]),
    [vehiclesListInit],
  );

  const electricCount = recommendations.filter((r) => r === "ELECTRIC").length;
  const pluginHybridCount = recommendations.filter(
    (r) => r === "PLUGIN_HYBRID",
  ).length;
  const electrifiableShare =
    recommendations.length > 0
      ? Math.round(
          ((electricCount + pluginHybridCount) / recommendations.length) * 100,
        )
      : 0;

  const pluralizedLabel = (key: string, count: number) =>
    t(`${key}.${count === 1 ? "one" : "other"}`);

  return (
    <StyledFlex>
      <StyledCardWrapper>
        <StyledShareCard>
          <DonutChartDS value={electrifiableShare} size="S" />
          <Spacer x={1} />
          <StyledColumn>
            <StyledCount>{electrifiableShare}%</StyledCount>
            <Spacer y={0.5} />
            <StyledCardLabel>
              {t("electrification.header.shareOfElectrifiable")}
            </StyledCardLabel>
          </StyledColumn>
        </StyledShareCard>
      </StyledCardWrapper>
      <Spacer x={1} />
      <StyledCardWrapper>
        <DataCard
          titleHeight=""
          title={
            <TableTagEnergyRecommendation
              energyRecommendation="ELECTRIC"
              text={t("electrification.energyRecommendation.ELECTRIC")}
            />
          }
          content={
            <>
              <StyledCount>{electricCount}</StyledCount>
              <Spacer y={0.5} />
              <StyledCardLabel>
                {pluralizedLabel(
                  "electrification.header.electricSwitchable",
                  electricCount,
                )}
              </StyledCardLabel>
            </>
          }
        />
      </StyledCardWrapper>
      <Spacer x={1} />
      <StyledCardWrapper>
        <DataCard
          titleHeight=""
          title={
            <TableTagEnergyRecommendation
              energyRecommendation="PLUGIN_HYBRID"
              text={t("electrification.energyRecommendation.PLUGIN_HYBRID")}
            />
          }
          content={
            <>
              <StyledCount>{pluginHybridCount}</StyledCount>
              <Spacer y={0.5} />
              <StyledCardLabel>
                {pluralizedLabel(
                  "electrification.header.hybridSwitchable",
                  pluginHybridCount,
                )}
              </StyledCardLabel>
            </>
          }
        />
      </StyledCardWrapper>
    </StyledFlex>
  );
};

const StyledFlex = styled.div`
  display: flex;
`;

const StyledCardWrapper = styled.div`
  flex: 1;
  display: flex;
`;

const StyledShareCard = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background-color: white;
  border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
  border-radius: 0.75rem;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCount = styled.div`
  ${typographies["Header/H1"]}
  color: ${colors["colors/text/black"]};
`;

const StyledCardLabel = styled.div`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/darkGrey"]};
`;
